import firebase from "firebase";
import { GAME_OVERRIDE } from "../../constants";
import { TournamentError } from "../../errors";
export class FirestoreTournamentRepository {
    db;
    createMatchSQL;
    serverTimestamp;
    matchesRef;
    tournamentsRef;
    constructor(db, createMatchSQL, serverTimestamp = () => firebase.firestore.FieldValue.serverTimestamp()) {
        this.db = db;
        this.createMatchSQL = createMatchSQL;
        this.serverTimestamp = serverTimestamp;
        this.matchesRef = this.db.collection("matches");
        this.tournamentsRef = this.db.collection("tournaments");
    }
    // Tournament Management
    async createTournament(tournament) {
        const doc = await this.tournamentsRef.add({
            ...tournament,
            deletedAt: null,
            createdAt: this.serverTimestamp(),
            updatedAt: this.serverTimestamp(),
        });
        return doc.id;
    }
    async updateTournament(tournamentId, tournament) {
        const tournamentRef = this.tournamentsRef.doc(tournamentId);
        await tournamentRef.set({
            ...tournament,
            updatedAt: this.serverTimestamp(),
        });
    }
    async deleteTournament(tournamentId) {
        await this.tournamentsRef.doc(tournamentId).update({
            deletedAt: this.serverTimestamp(),
        });
    }
    async getTournament(tournamentId) {
        const doc = await this.tournamentsRef.doc(tournamentId).get();
        if (!doc.exists) {
            throw new Error(`Tournament ${tournamentId} not found`);
        }
        return {
            id: doc.id,
            ...doc.data(),
        };
    }
    async updateCurrentRound(tournamentId, roundNumber) {
        const tournamentRef = this.tournamentsRef.doc(tournamentId);
        await tournamentRef.update({
            currentRound: roundNumber,
            updatedAt: this.serverTimestamp(),
        });
    }
    async updatePlayerCount(tournamentId, playerCount) {
        const tournamentRef = this.tournamentsRef.doc(tournamentId);
        await tournamentRef.update({
            playerCount,
            updatedAt: this.serverTimestamp(),
        });
    }
    async updatePlayerUsernames(tournamentId, playerUsernames) {
        const tournamentRef = this.tournamentsRef.doc(tournamentId);
        await tournamentRef.update({
            playerUsernames,
            updatedAt: this.serverTimestamp(),
        });
    }
    async updateStartTime(tournamentId, startTime) {
        const tournamentRef = this.tournamentsRef.doc(tournamentId);
        await tournamentRef.update({
            startTime: firebase.firestore.Timestamp.fromDate(startTime),
            updatedAt: this.serverTimestamp(),
        });
    }
    async broadcastMessage(tournamentId, message) {
        const tournamentRef = this.tournamentsRef.doc(tournamentId);
        await tournamentRef.update({
            message,
            updatedAt: this.serverTimestamp(),
        });
    }
    async finishTournament(tournamentId) {
        const tournamentRef = this.tournamentsRef.doc(tournamentId);
        await tournamentRef.update({
            status: "finished",
            automate: false,
            currentRound: firebase.firestore.FieldValue.increment(1),
            finishedAt: this.serverTimestamp(),
            updatedAt: this.serverTimestamp(),
        });
    }
    // Player Management
    async createPlayer(tournamentId, player) {
        const tournamentRef = this.tournamentsRef.doc(tournamentId);
        const playerRef = tournamentRef.collection("players").doc(player.username);
        const batch = this.db.batch();
        batch.set(playerRef, {
            ...player,
            createdAt: this.serverTimestamp(),
            updatedAt: this.serverTimestamp(),
        });
        const tournament = await this.getTournament(tournamentId);
        const playerUsernames = tournament.playerUsernames || [];
        if (!playerUsernames.includes(player.username)) {
            batch.update(tournamentRef, {
                playerCount: tournament.playerCount + 1,
                playerUsernames: [...playerUsernames, player.username],
                updatedAt: this.serverTimestamp(),
            });
        }
        await batch.commit();
    }
    /**
     * Create multiple players in a single batch operation for better performance
     */
    async createPlayers(tournamentId, players) {
        if (!players.length)
            return;
        const tournamentRef = this.tournamentsRef.doc(tournamentId);
        const batch = this.db.batch();
        // Add all players to the batch
        for (const player of players) {
            const playerRef = tournamentRef
                .collection("players")
                .doc(player.username);
            batch.set(playerRef, {
                ...player,
                createdAt: this.serverTimestamp(),
                updatedAt: this.serverTimestamp(),
            });
        }
        // Update tournament with all player usernames and count in one operation
        const tournament = await this.getTournament(tournamentId);
        const existingUsernames = tournament.playerUsernames || [];
        const newUsernames = players.map((p) => p.username);
        const allUsernames = [...new Set([...existingUsernames, ...newUsernames])];
        batch.update(tournamentRef, {
            playerCount: allUsernames.length,
            playerUsernames: allUsernames,
            updatedAt: this.serverTimestamp(),
        });
        await batch.commit();
    }
    async getPlayer(tournamentId, username) {
        const doc = await this.tournamentsRef
            .doc(tournamentId)
            .collection("players")
            .doc(username)
            .get();
        if (!doc.exists) {
            return null;
        }
        return {
            username,
            ...doc.data(),
        };
    }
    async getPlayers(tournamentId) {
        const snapshot = await this.tournamentsRef
            .doc(tournamentId)
            .collection("players")
            .where("deletedAt", "==", null)
            .get();
        return snapshot.docs.map((doc) => {
            const data = doc.data();
            return {
                id: doc.id,
                active: !data.deletedAt,
                ...data,
            };
        });
    }
    async updatePlayer(tournamentId, username, data) {
        const playerRef = this.tournamentsRef
            .doc(tournamentId)
            .collection("players")
            .doc(username);
        await playerRef.update({
            ...data,
            updatedAt: this.serverTimestamp(),
        });
    }
    async removePlayer(tournamentId, username, softDelete = true) {
        const tournamentRef = this.tournamentsRef.doc(tournamentId);
        const playerRef = tournamentRef.collection("players").doc(username);
        const batch = this.db.batch();
        if (softDelete) {
            batch.update(playerRef, {
                deletedAt: this.serverTimestamp(),
                updatedAt: this.serverTimestamp(),
            });
        }
        else {
            batch.delete(playerRef);
        }
        batch.update(tournamentRef, {
            playerCount: firebase.firestore.FieldValue.increment(-1),
            playerUsernames: firebase.firestore.FieldValue.arrayRemove(username),
            updatedAt: this.serverTimestamp(),
        });
        await batch.commit();
    }
    // Game Management
    async createGame(tournamentId, game) {
        const tournamentRef = this.tournamentsRef.doc(tournamentId);
        const gamesCollectionRef = tournamentRef.collection("games");
        await gamesCollectionRef.doc(game.gameId).set({
            ...game,
            createdAt: this.serverTimestamp(),
        });
    }
    /**
     * Create multiple games in a single batch operation for better performance
     */
    async createGames(tournamentId, games) {
        if (!games.length)
            return;
        const tournamentRef = this.tournamentsRef.doc(tournamentId);
        const batch = this.db.batch();
        // Add all games to the batch
        for (const game of games) {
            const gameRef = tournamentRef.collection("games").doc(game.gameId);
            batch.set(gameRef, {
                ...game,
                createdAt: this.serverTimestamp(),
            });
        }
        await batch.commit();
    }
    async getGame(tournamentId, gameId) {
        const snapshot = await this.tournamentsRef
            .doc(tournamentId)
            .collection("games")
            .where("gameId", "==", gameId)
            .limit(1)
            .get();
        if (snapshot.empty) {
            return null;
        }
        const doc = snapshot.docs[0];
        return {
            id: doc.id,
            ...doc.data(),
        };
    }
    async getGames(tournamentId) {
        const snapshot = await this.tournamentsRef
            .doc(tournamentId)
            .collection("games")
            .orderBy("roundNumber")
            .get();
        return snapshot.docs
            .sort((doc1, doc2) => doc1.data().board - doc2.data().board)
            .map((doc) => ({
            id: doc.id,
            ...doc.data(),
        }));
    }
    async updateGame(tournamentId, gameId, data) {
        const snapshot = await this.tournamentsRef
            .doc(tournamentId)
            .collection("games")
            .where("gameId", "==", gameId)
            .limit(1)
            .get();
        if (snapshot.empty) {
            throw new Error(`Game ${gameId} not found in tournament ${tournamentId}`);
        }
        const doc = snapshot.docs[0];
        await doc.ref.update({
            ...data,
            updatedAt: this.serverTimestamp(),
        });
    }
    async updateGames(tournamentId, games) {
        if (!games.length)
            return;
        const tournamentRef = this.tournamentsRef.doc(tournamentId);
        const batch = this.db.batch();
        // Add all games to the batch
        for (const game of games) {
            const gameRef = tournamentRef.collection("games").doc(game.gameId);
            batch.update(gameRef, {
                ...game,
                updatedAt: this.serverTimestamp(),
            });
        }
        await batch.commit();
    }
    async startGame(tournamentId, game) {
        const matchRef = this.matchesRef.doc(game.matchGUID);
        const docSnapshot = await matchRef.get();
        if (!docSnapshot) {
            throw new TournamentError(tournamentId, game.roundNumber, "startGame", `Error getting firestore match document ${game.matchGUID}`);
        }
        // Log if match already exists
        if (docSnapshot.exists) {
            console.log("Match already exists", docSnapshot.data());
        }
        const gameRef = this.tournamentsRef
            .doc(tournamentId)
            .collection("games")
            .doc(game.gameId);
        await gameRef.update({
            startTime: this.serverTimestamp(),
            updatedAt: this.serverTimestamp(),
            matchRef: matchRef,
        });
    }
    async finishGame(tournamentId, gameId, result) {
        const gameRef = this.tournamentsRef
            .doc(tournamentId)
            .collection("games")
            .doc(gameId);
        await gameRef.update({
            ...result,
            finishTime: result.finishTime || this.serverTimestamp(),
            updatedAt: this.serverTimestamp(),
        });
    }
    // Round Management
    async getRounds(tournamentId) {
        const snapshot = await this.tournamentsRef
            .doc(tournamentId)
            .collection("rounds")
            .get();
        return snapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
        }));
    }
    async getRound(tournamentId, roundNumber) {
        const roundDoc = await this.tournamentsRef
            .doc(tournamentId)
            .collection("rounds")
            .doc(roundNumber.toString())
            .get();
        if (!roundDoc.exists) {
            return null;
        }
        return {
            id: roundDoc.id,
            ...roundDoc.data(),
        };
    }
    async createRound(tournamentId, roundData) {
        const tournamentRef = this.tournamentsRef.doc(tournamentId);
        const roundRef = tournamentRef.collection("rounds").doc(roundData.id);
        await roundRef.set({
            ...roundData,
            createdAt: this.serverTimestamp(),
            updatedAt: this.serverTimestamp(),
        });
    }
    async updateRoundStartTime(tournamentId, roundNumber, startTime) {
        const roundsRef = this.tournamentsRef
            .doc(tournamentId)
            .collection("rounds");
        const roundDoc = await roundsRef.doc(roundNumber.toString()).get();
        if (roundDoc.exists) {
            console.log("update round", roundNumber, roundDoc);
            await roundDoc.ref.update({
                startTime: startTime,
                updatedAt: this.serverTimestamp(),
            });
        }
        else {
            console.log("create round", roundNumber, roundDoc);
            await roundDoc.ref.set({
                roundNumber: roundNumber,
                startTime: startTime,
                createdAt: this.serverTimestamp(),
                updatedAt: this.serverTimestamp(),
            });
        }
    }
    async updateRoundFinishTime(tournamentId, roundNumber, finishTime) {
        const roundRef = this.tournamentsRef
            .doc(tournamentId)
            .collection("rounds")
            .doc(roundNumber.toString());
        const updateData = {
            updatedAt: this.serverTimestamp(),
        };
        if (finishTime === null) {
            updateData.finishTime = firebase.firestore.FieldValue.delete();
        }
        else {
            updateData.finishTime = firebase.firestore.Timestamp.fromDate(finishTime);
        }
        await roundRef.update(updateData);
    }
    async updateRoundFinalStandings(tournamentId, roundNumber, standings) {
        const roundRef = this.tournamentsRef
            .doc(tournamentId)
            .collection("rounds")
            .doc(roundNumber.toString());
        const roundDoc = await roundRef.get();
        const updateData = {
            standingsAfter: standings,
            updatedAt: this.serverTimestamp(),
        };
        if (roundDoc.exists && !roundDoc.data()?.finishTime) {
            updateData.finishTime = this.serverTimestamp();
        }
        await roundRef.update(updateData);
    }
    async updateNextRoundInitialStandings(tournamentId, roundNumber, standings) {
        const roundRef = this.tournamentsRef
            .doc(tournamentId)
            .collection("rounds")
            .doc(roundNumber.toString());
        await roundRef.update({
            standings: standings,
            updatedAt: this.serverTimestamp(),
        });
    }
    // Event Tracking
    async storeEvent(event) {
        const tournamentRef = this.tournamentsRef.doc(event.tournamentId);
        const eventsRef = tournamentRef.collection("events");
        await eventsRef.add({
            ...event,
            createdAt: this.serverTimestamp(),
        });
    }
    async getEvents(tournamentId, fromTimestamp) {
        const tournamentRef = this.tournamentsRef.doc(tournamentId);
        const eventsRef = tournamentRef.collection("events");
        const snapshot = await eventsRef
            .where("timestamp", ">=", fromTimestamp)
            .orderBy("timestamp")
            .get();
        return snapshot.docs.map((doc) => {
            const data = doc.data();
            return {
                type: data.type,
                tournamentId: data.tournamentId,
                timestamp: data.timestamp,
                data: data.data,
                id: doc.id,
            };
        });
    }
    async getMatch(matchId) {
        const matchRef = this.matchesRef.doc(matchId);
        const doc = await matchRef.get();
        if (!doc.exists) {
            return null;
        }
        return { id: doc.id, ...doc.data() };
    }
    async getMatchMoves(matchId) {
        const matchRef = this.matchesRef.doc(matchId);
        const snapshot = await matchRef
            .collection("moves")
            .orderBy("createdAt")
            .get();
        return snapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
        }));
    }
    async createMatch(matchData) {
        await this.createMatchWithSQL(matchData);
        const matchRef = this.matchesRef.doc(matchData.match_id);
        await matchRef.set({
            ...matchData,
            increment: matchData.increment ?? 0,
            gameOverride: matchData.gameOverride ?? GAME_OVERRIDE.TOURNAMENT,
            playerIds: [matchData.player1.username, matchData.player2.username],
            status: "live",
            result: null,
            createdAt: this.serverTimestamp(),
            updatedAt: this.serverTimestamp(),
            startedAt: this.serverTimestamp(),
        });
        return matchData.match_id;
    }
    async createMatchWithSQL(params) {
        // Create match in SQL first
        const sqlMatchId = await this.createMatchSQL({
            match_id: params.match_id,
            game_override: params.gameOverride ?? GAME_OVERRIDE.TOURNAMENT,
            player_1_id: params.player1.username,
            player_2_id: params.player2.username,
            duration: params.duration,
            increment: params.increment,
            variant: params.variant,
        });
        if (!sqlMatchId) {
            throw new TournamentError(params.tournamentId, params.roundNumber, "createMatchWithSQL", `Failed to create SQL match ${params.match_id}`);
        }
        return sqlMatchId;
    }
    async updateMatch(matchId, matchData) {
        const matchRef = this.matchesRef.doc(matchId);
        await matchRef.update({
            ...matchData,
            updatedAt: this.serverTimestamp(),
        });
    }
}
